/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BeakerIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  StopCircleIcon,
  ChartBarIcon,
  TableCellsIcon,
  EyeIcon,
  TrashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import {
  experimentService,
  runService,
  ExperimentDetail as ExperimentDetailType,
  RunWithMetrics,
  Metric,
} from '../../services/registryApi';
import toast from 'react-hot-toast';

const CHART_COLORS = [
  '#F97316', // orange (primary)
  '#10B981', // green
  '#3B82F6', // blue
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F59E0B', // amber
];

const STATUS_COLORS: Record<string, string> = {
  running: '#3B82F6',
  completed: '#10B981',
  failed: '#EF4444',
  killed: '#6B7280',
};

const RunStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
    running: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: PlayIcon },
    completed: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircleIcon },
    failed: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircleIcon },
    killed: { bg: 'bg-gray-700', text: 'text-gray-300', icon: StopCircleIcon },
  };

  const { bg, text, icon: Icon } = config[status] || config.running;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const formatDuration = (ms: number): string => {
  if (!ms) return '-';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
};

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
}> = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-gray-900 rounded-xl border border-gray-700 p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-400">{title}</p>
        <p className="text-2xl font-bold text-white mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const ExperimentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [experimentDetail, setExperimentDetail] = useState<ExperimentDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedRuns, setSelectedRuns] = useState<string[]>([]);
  const [metricsHistory, setMetricsHistory] = useState<Record<string, Metric[]>>({});
  const [selectedMetric, setSelectedMetric] = useState<string>('');
  const [viewMode, setViewMode] = useState<'table' | 'chart' | 'params'>('table');
  const [deletingRun, setDeletingRun] = useState<string | null>(null);

  const fetchExperimentDetail = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await experimentService.getDetail(id, { limit: 100 });
      setExperimentDetail(response.data);
      // Auto-select first metric if available
      if (response.data.metric_keys?.length > 0 && !selectedMetric) {
        setSelectedMetric(response.data.metric_keys[0]);
      }
    } catch (error) {
      console.error('Failed to fetch experiment detail:', error);
      toast.error('Failed to load experiment details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperimentDetail();
  }, [id]);

  // Fetch metric history for selected runs
  useEffect(() => {
    const fetchMetricHistory = async () => {
      if (selectedRuns.length === 0 || !selectedMetric) {
        setMetricsHistory({});
        return;
      }

      try {
        const response = await runService.getMultiRunMetricHistory(selectedRuns, selectedMetric);
        setMetricsHistory(response.data.history || {});
      } catch (error) {
        console.error('Failed to fetch metric history:', error);
        // Fallback: fetch individually
        const history: Record<string, Metric[]> = {};
        for (const runId of selectedRuns) {
          try {
            const res = await runService.getMetricHistory(runId, selectedMetric);
            history[runId] = res.data.metrics || [];
          } catch (e) {
            console.error(`Failed to fetch metrics for run ${runId}:`, e);
          }
        }
        setMetricsHistory(history);
      }
    };

    fetchMetricHistory();
  }, [selectedRuns, selectedMetric]);

  const handleRunSelect = (runId: string) => {
    setSelectedRuns((prev) =>
      prev.includes(runId) ? prev.filter((id) => id !== runId) : [...prev, runId]
    );
  };

  const handleSelectAll = () => {
    if (!experimentDetail) return;
    if (selectedRuns.length === experimentDetail.runs.length) {
      setSelectedRuns([]);
    } else {
      setSelectedRuns(experimentDetail.runs.map((r) => r.run.id));
    }
  };

  const handleDeleteRun = async (runId: string) => {
    if (!window.confirm('Are you sure you want to delete this run? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingRun(runId);
      await runService.delete(runId);
      toast.success('Run deleted successfully');
      setSelectedRuns((prev) => prev.filter((id) => id !== runId));
      fetchExperimentDetail();
    } catch (error) {
      console.error('Failed to delete run:', error);
      toast.error('Failed to delete run');
    } finally {
      setDeletingRun(null);
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    if (!experimentDetail) return null;

    const runs = experimentDetail.runs;
    const totalDuration = runs.reduce((sum, r) => sum + (r.run.duration_ms || 0), 0);
    const avgDuration = runs.length > 0 ? totalDuration / runs.length : 0;

    return {
      totalRuns: experimentDetail.total_runs,
      avgDuration: formatDuration(avgDuration),
      runsByStatus: experimentDetail.runs_by_status || {},
    };
  }, [experimentDetail]);

  // Prepare chart data for metric comparison
  const chartData = useMemo(() => {
    if (!selectedMetric || selectedRuns.length === 0 || !experimentDetail) return [];

    const stepSet = new Set<number>();
    selectedRuns.forEach((runId) => {
      metricsHistory[runId]?.forEach((m) => stepSet.add(m.step));
    });

    const steps = Array.from(stepSet).sort((a, b) => a - b);

    return steps.map((step) => {
      const point: Record<string, number | string> = { step };
      selectedRuns.forEach((runId) => {
        const runData = experimentDetail.runs.find((r) => r.run.id === runId);
        const metric = metricsHistory[runId]?.find((m) => m.step === step);
        if (metric) {
          point[runData?.run.name || runId.slice(0, 8)] = metric.value;
        }
      });
      return point;
    });
  }, [selectedMetric, selectedRuns, metricsHistory, experimentDetail]);

  // Prepare runs by status chart data
  const statusChartData = useMemo(() => {
    if (!stats?.runsByStatus) return [];
    return Object.entries(stats.runsByStatus).map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      color: STATUS_COLORS[status] || '#6B7280',
    }));
  }, [stats]);

  // Prepare params comparison data
  const paramsComparison = useMemo(() => {
    if (!experimentDetail || selectedRuns.length === 0) return { paramKeys: [], data: [] };

    const selectedRunsData = experimentDetail.runs.filter((r) => selectedRuns.includes(r.run.id));
    const allParamKeys = new Set<string>();

    selectedRunsData.forEach((runData) => {
      Object.keys(runData.params || {}).forEach((key) => allParamKeys.add(key));
    });

    const paramKeys = Array.from(allParamKeys).sort();
    const data = selectedRunsData.map((runData) => ({
      runName: runData.run.name || runData.run.id.slice(0, 8),
      runId: runData.run.id,
      params: runData.params || {},
    }));

    return { paramKeys, data };
  }, [experimentDetail, selectedRuns]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-gray-700 border-t-primary-500"></div>
      </div>
    );
  }

  if (!experimentDetail) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <BeakerIcon className="mx-auto h-12 w-12 text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-white">Experiment not found</h3>
          <button
            onClick={() => navigate('/registry/experiments')}
            className="mt-4 text-primary-500 hover:text-primary-400"
          >
            Back to experiments
          </button>
        </div>
      </div>
    );
  }

  const { experiment, runs, metric_keys, param_keys } = experimentDetail;

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/registry/experiments')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Experiments
          </button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <BeakerIcon className="w-8 h-8 text-primary-500" />
                <h1 className="text-2xl font-bold text-white">{experiment.name}</h1>
              </div>
              {experiment.description && (
                <p className="mt-2 text-gray-400">{experiment.description}</p>
              )}
            </div>
            <button
              onClick={fetchExperimentDetail}
              className="p-2 text-gray-600 hover:text-gray-300"
              title="Refresh"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-4 mt-4 text-sm text-gray-400">
            <span>Created: {new Date(experiment.created_at).toLocaleDateString()}</span>
            {experiment.last_run_at && (
              <span>Last run: {new Date(experiment.last_run_at).toLocaleString()}</span>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Runs"
            value={stats?.totalRuns || 0}
            icon={PlayIcon}
            color="bg-primary-500"
          />
          <StatCard
            title="Completed"
            value={stats?.runsByStatus?.completed || 0}
            icon={CheckCircleIcon}
            color="bg-green-500"
            subtitle={stats?.totalRuns ? `${Math.round(((stats.runsByStatus?.completed || 0) / stats.totalRuns) * 100)}% success rate` : undefined}
          />
          <StatCard
            title="Failed"
            value={stats?.runsByStatus?.failed || 0}
            icon={XCircleIcon}
            color="bg-red-500"
          />
          <StatCard
            title="Avg Duration"
            value={stats?.avgDuration || '-'}
            icon={ClockIcon}
            color="bg-purple-500"
          />
        </div>

        {/* Charts Row */}
        {runs.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Runs by Status */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-4">Runs by Status</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Latest Metrics Summary */}
            <div className="lg:col-span-2 bg-gray-900 rounded-xl border border-gray-700 p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-4">Latest Metrics (Recent Runs)</h3>
              {metric_keys && metric_keys.length > 0 ? (
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={runs.slice(0, 5).map((runData) => ({
                        name: runData.run.name || runData.run.id.slice(0, 8),
                        ...runData.latest_metrics,
                      }))}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3d" />
                      <XAxis type="number" stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} />
                      <YAxis dataKey="name" type="category" width={80} stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1a1a28',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                        }}
                      />
                      <Legend />
                      {metric_keys.slice(0, 3).map((key, index) => (
                        <Bar key={key} dataKey={key} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-gray-400">
                  No metrics logged yet
                </div>
              )}
            </div>
          </div>
        )}

        {/* View Toggle */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Training Runs</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'table'
                  ? 'bg-primary-500/20 text-primary-500'
                  : 'text-gray-600 hover:text-gray-300'
              }`}
              title="Table View"
            >
              <TableCellsIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'chart'
                  ? 'bg-primary-500/20 text-primary-500'
                  : 'text-gray-600 hover:text-gray-300'
              }`}
              title="Chart View"
            >
              <ChartBarIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('params')}
              className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                viewMode === 'params'
                  ? 'bg-primary-500/20 text-primary-500'
                  : 'text-gray-600 hover:text-gray-300'
              }`}
              title="Compare Parameters"
            >
              Params
            </button>
          </div>
        </div>

        {/* Runs Table */}
        <div className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden mb-6">
          {runs.length === 0 ? (
            <div className="text-center py-12">
              <PlayIcon className="mx-auto h-12 w-12 text-gray-600" />
              <h3 className="mt-2 text-sm font-medium text-white">No runs yet</h3>
              <p className="mt-1 text-sm text-gray-400">
                Start tracking runs using the Python SDK.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedRuns.length === runs.length}
                        onChange={handleSelectAll}
                        className="rounded border-gray-600 text-primary-500 focus:ring-primary-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Run Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Duration
                    </th>
                    {metric_keys?.slice(0, 3).map((key) => (
                      <th key={key} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        {key}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Started
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {runs.map((runData) => (
                    <tr
                      key={runData.run.id}
                      className={`hover:bg-gray-800 ${
                        selectedRuns.includes(runData.run.id) ? 'bg-primary-500/10' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRuns.includes(runData.run.id)}
                          onChange={() => handleRunSelect(runData.run.id)}
                          className="rounded border-gray-600 text-primary-500 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-white">
                          {runData.run.name || runData.run.id.slice(0, 8)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <RunStatusBadge status={runData.run.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        {formatDuration(runData.run.duration_ms)}
                      </td>
                      {metric_keys?.slice(0, 3).map((key) => (
                        <td key={key} className="px-4 py-3 text-sm text-gray-300 font-mono">
                          {runData.latest_metrics?.[key]?.toFixed(4) || '-'}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-sm text-gray-300">
                        {runData.run.start_time ? new Date(runData.run.start_time * 1000).toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/registry/runs/${runData.run.id}`)}
                            className="text-primary-500 hover:text-primary-400"
                            title="View run details"
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteRun(runData.run.id)}
                            disabled={deletingRun === runData.run.id}
                            className="text-red-500 hover:text-red-400 disabled:opacity-50"
                            title="Delete run"
                          >
                            {deletingRun === runData.run.id ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
                            ) : (
                              <TrashIcon className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Metrics Chart */}
        {viewMode === 'chart' && selectedRuns.length > 0 && (
          <div className="bg-gray-900 rounded-xl border border-gray-700 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Metrics Comparison</h3>
              {metric_keys && metric_keys.length > 0 && (
                <select
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                  className="px-3 py-1.5 border border-gray-600 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-primary-500"
                >
                  {metric_keys.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {chartData.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                No metrics data available for the selected runs
              </div>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3d" />
                    <XAxis
                      dataKey="step"
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF' }}
                      label={{ value: 'Step', position: 'insideBottom', offset: -5, fill: '#9CA3AF' }}
                    />
                    <YAxis
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF' }}
                      label={{ value: selectedMetric, angle: -90, position: 'insideLeft', fill: '#9CA3AF' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a28',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff',
                      }}
                    />
                    <Legend />
                    {selectedRuns.map((runId, index) => {
                      const runData = runs.find((r) => r.run.id === runId);
                      const name = runData?.run.name || runId.slice(0, 8);
                      return (
                        <Line
                          key={runId}
                          type="monotone"
                          dataKey={name}
                          stroke={CHART_COLORS[index % CHART_COLORS.length]}
                          strokeWidth={2}
                          dot={false}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Params Comparison */}
        {viewMode === 'params' && selectedRuns.length > 0 && (
          <div className="bg-gray-900 rounded-xl border border-gray-700 p-6 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Parameters Comparison</h3>
            {paramsComparison.paramKeys.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                No parameters logged for the selected runs
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase sticky left-0 bg-gray-800">
                        Parameter
                      </th>
                      {paramsComparison.data.map((runData) => (
                        <th
                          key={runData.runId}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase"
                        >
                          {runData.runName}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {paramsComparison.paramKeys.map((paramKey) => (
                      <tr key={paramKey} className="hover:bg-gray-800">
                        <td className="px-4 py-3 text-sm font-medium text-white sticky left-0 bg-gray-900">
                          {paramKey}
                        </td>
                        {paramsComparison.data.map((runData) => {
                          const value = runData.params[paramKey];
                          // Check if values differ across runs
                          const values = paramsComparison.data.map((d) => d.params[paramKey]);
                          const isDifferent = new Set(values).size > 1;
                          return (
                            <td
                              key={runData.runId}
                              className={`px-4 py-3 text-sm font-mono ${
                                isDifferent
                                  ? 'text-primary-500 font-medium'
                                  : 'text-gray-300'
                              }`}
                            >
                              {value || '-'}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Selection hint */}
        {(viewMode === 'chart' || viewMode === 'params') && selectedRuns.length === 0 && (
          <div className="bg-primary-500/20 border border-primary-500/30 rounded-xl p-4 text-center">
            <p className="text-primary-400">
              Select runs from the table above to compare {viewMode === 'chart' ? 'metrics' : 'parameters'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExperimentDetail;
